import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { measureDefinitionsService } from '../services';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/sonner';
import { ArrowLeft, Plus, Pencil, Trash2, Check, X, Loader2, Ruler } from 'lucide-react';
import { getApiError } from '@/lib/utils';

// Use centralized measureDefinitionsService from services/index.js
const measureService = measureDefinitionsService;

export default function MeasureDefinitionsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [measures, setMeasures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', default_ml: '' });
  const [addMode, setAddMode] = useState(false);
  const [newForm, setNewForm] = useState({ name: '', default_ml: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await measureService.list();
      setMeasures(data || []);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load measures', variant: 'destructive' });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!newForm.name.trim()) {
      toast({ title: 'Name required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const result = await measureService.create({
        name: newForm.name.trim(),
        default_ml: newForm.default_ml ? Number(newForm.default_ml) : null,
        sort_order: measures.length,
      });
      setMeasures(prev => [...prev, result]);
      setNewForm({ name: '', default_ml: '' });
      setAddMode(false);
      toast({ title: 'Measure added', description: result.name });
    } catch (err) {
      toast({ title: 'Failed', description: getApiError(err, 'Error'), variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const startEdit = (m) => {
    setEditingId(m.id);
    setEditForm({ name: m.name, default_ml: m.default_ml ?? '' });
  };

  const handleUpdate = async () => {
    if (!editForm.name.trim()) {
      toast({ title: 'Name required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const result = await measureService.update(editingId, {
        name: editForm.name.trim(),
        default_ml: editForm.default_ml ? Number(editForm.default_ml) : null,
      });
      setMeasures(prev => prev.map(m => m.id === editingId ? result : m));
      setEditingId(null);
      toast({ title: 'Measure updated' });
    } catch (err) {
      toast({ title: 'Failed', description: getApiError(err, 'Error'), variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    try {
      await measureService.remove(id);
      setMeasures(prev => prev.filter(m => m.id !== id));
      toast({ title: 'Deleted', description: `${name} removed` });
    } catch (err) {
      toast({ title: 'Failed', description: getApiError(err, 'Error'), variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6" data-testid="measure-definitions-page">
      <Toaster />

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/stocktaking/dashboard')} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Ruler className="h-6 w-6 text-amber-600" />
              Measure Definitions
            </h1>
            <p className="text-sm text-muted-foreground">Master list of selling measure types used across products and site inventory</p>
          </div>
        </div>
        <Button onClick={() => setAddMode(true)} disabled={addMode} data-testid="add-measure-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Add Measure
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Measures</CardTitle>
          <CardDescription>Define the standard serving measures (e.g. Shot, Glass, Pint). These are available when setting product measures in Site Inventory.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead className="text-xs">Measure Name</TableHead>
                    <TableHead className="text-xs text-right">Default Size (ml)</TableHead>
                    <TableHead className="w-[100px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {measures.map(m => (
                    <TableRow key={m.id} data-testid={`measure-row-${m.id}`}>
                      {editingId === m.id ? (
                        <>
                          <TableCell>
                            <Input className="h-8 text-sm" value={editForm.name}
                              onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                              data-testid={`edit-measure-name-${m.id}`} />
                          </TableCell>
                          <TableCell>
                            <Input className="h-8 text-sm text-right w-24 ml-auto" type="number" step="0.01"
                              value={editForm.default_ml}
                              onChange={e => setEditForm(f => ({ ...f, default_ml: e.target.value }))}
                              placeholder="Optional"
                              data-testid={`edit-measure-ml-${m.id}`} />
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1 justify-end">
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleUpdate} disabled={saving}>
                                <Check className="h-3.5 w-3.5 text-green-600" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditingId(null)}>
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell className="font-medium text-sm">{m.name}</TableCell>
                          <TableCell className="text-sm text-right tabular-nums">
                            {m.default_ml ? `${m.default_ml}ml` : <span className="text-muted-foreground text-xs">—</span>}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1 justify-end">
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(m)}
                                data-testid={`edit-measure-btn-${m.id}`}>
                                <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(m.id, m.name)}
                                data-testid={`delete-measure-btn-${m.id}`}>
                                <Trash2 className="h-3.5 w-3.5 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}

                  {/* Add new row */}
                  {addMode && (
                    <TableRow data-testid="new-measure-row">
                      <TableCell>
                        <Input className="h-8 text-sm" placeholder="e.g. Shot, Glass, Pint"
                          value={newForm.name}
                          onChange={e => setNewForm(f => ({ ...f, name: e.target.value }))}
                          autoFocus
                          data-testid="new-measure-name" />
                      </TableCell>
                      <TableCell>
                        <Input className="h-8 text-sm text-right w-24 ml-auto" type="number" step="0.01"
                          placeholder="ml"
                          value={newForm.default_ml}
                          onChange={e => setNewForm(f => ({ ...f, default_ml: e.target.value }))}
                          data-testid="new-measure-ml" />
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 justify-end">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleAdd} disabled={saving}>
                            <Check className="h-3.5 w-3.5 text-green-600" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setAddMode(false); setNewForm({ name: '', default_ml: '' }); }}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}

                  {measures.length === 0 && !addMode && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-8 text-muted-foreground text-sm">
                        No measures defined yet. Click "Add Measure" to create your first one.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
